import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  ListItemButton,
  Checkbox,
  Chip,
  Tabs,
  Tab,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { usePermissions } from "../../hooks/usePermissions";
import { CAPABILITIES } from "../../auth/permissions";
import RolePermissionMatrix from "./RolePermissionMatrix";
import { getRoleMeta, groupRoles } from "./roleDisplay";
import UserAvatar from "../../components/common/UserAvatar";
import ViewRoleUserDialog from "./ViewRoleUserDialog";

const getUserDisplayName = (user) => {
  const first = user?.firstName || user?.firstname || "";
  const last = user?.lastName || user?.lastname || "";
  const full = `${first} ${last}`.trim();
  return full || user?.userName || user?.email || "Unknown user";
};

const isUserActive = (user) => Number(user?.active) === 1;

const RoleManagement = () => {
  const { hasPermission } = usePermissions();
  const canAssignAd2 = hasPermission(CAPABILITIES.adminAssignAd2, "X");
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [usersInRole, setUsersInRole] = useState([]);
  const [allUsersInRole, setAllUsersInRole] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openAddRoleDialog, setOpenAddRoleDialog] = useState(false);
  const [openAddUsersDialog, setOpenAddUsersDialog] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [newRoleName, setNewRoleName] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [deleteRoleConfirm, setDeleteRoleConfirm] = useState({ open: false, roleName: null });
  const [removeUserConfirm, setRemoveUserConfirm] = useState({ open: false, userId: null, userName: null });
  const [viewUser, setViewUser] = useState({ open: false, loading: false, error: "", user: null });
  const [sortColumn, setSortColumn] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [rightTab, setRightTab] = useState(0);
  const [catalog, setCatalog] = useState([]);
  const [levelsByCapability, setLevelsByCapability] = useState({});
  const [savedLevels, setSavedLevels] = useState({});
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionsSaving, setPermissionsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    fetchRoles();
    fetchCatalog();
  }, []);

  useEffect(() => {
    if (selectedRole) {
      fetchUsersInRole(selectedRole.name);
      fetchRolePermissions(selectedRole.name);
    }
  }, [selectedRole]);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await fetch("/api/v1/roles", {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("accessToken")}`,
        },
      });
      if (!response.ok) throw new Error("Failed to fetch roles");
      const data = await response.json();
      setRoles(data);
      if (data.length > 0 && !selectedRole) {
        setSelectedRole(data[0]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const authHeaders = () => ({
    Authorization: `Bearer ${sessionStorage.getItem("accessToken")}`,
  });

  const grantsToLevelMap = (grants) => {
    const next = {};
    (grants || []).forEach((grant) => {
      if (grant?.id && grant.level && grant.level !== "—") {
        next[grant.id] = grant.level;
      }
    });
    return next;
  };

  const fetchCatalog = async () => {
    try {
      const response = await fetch("/api/v1/permissions/catalog", {
        headers: authHeaders(),
      });
      if (!response.ok) throw new Error("Failed to fetch permission catalog");
      const data = await response.json();
      setCatalog(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchRolePermissions = async (roleName) => {
    try {
      setPermissionsLoading(true);
      const response = await fetch(`/api/v1/roles/${encodeURIComponent(roleName)}/permissions`, {
        headers: authHeaders(),
      });
      if (!response.ok) throw new Error("Failed to fetch role permissions");
      const data = await response.json();
      const mapped = grantsToLevelMap(data.grants);
      setLevelsByCapability(mapped);
      setSavedLevels(mapped);
    } catch (err) {
      setError(err.message);
    } finally {
      setPermissionsLoading(false);
    }
  };

  const handlePermissionLevelChange = (capabilityId, level) => {
    setLevelsByCapability((prev) => {
      const next = { ...prev };
      if (!level || level === "—") {
        delete next[capabilityId];
      } else {
        next[capabilityId] = level;
      }
      return next;
    });
  };

  const handleSavePermissions = async () => {
    if (!selectedRole) {
      return;
    }

    try {
      setPermissionsSaving(true);
      const grants = catalog.map((item) => ({
        id: item.id,
        level: levelsByCapability[item.id] || "—",
      }));
      const response = await fetch(`/api/v1/roles/${encodeURIComponent(selectedRole.name)}/permissions`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify({ grants }),
      });
      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.message || "Failed to save role permissions");
      }
      const data = await response.json();
      const mapped = grantsToLevelMap(data.grants);
      setLevelsByCapability(mapped);
      setSavedLevels(mapped);
      setSuccessMessage(`Permissions saved for ${selectedRole.name}.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setPermissionsSaving(false);
    }
  };

  const fetchUsersInRole = async (roleName) => {
    try {
      const response = await fetch(`/api/v1/roles/${roleName}/users`, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("accessToken")}`,
        },
      });
      if (!response.ok) throw new Error("Failed to fetch users in role");
      const data = await response.json();
      setAllUsersInRole(data);
      sortUsers(data, sortColumn, sortDirection);
    } catch (err) {
      setError(err.message);
    }
  };

  const sortUsers = (data, column, direction) => {
    const sorted = [...data].sort((a, b) => {
      let valueA, valueB;

      switch (column) {
        case "email":
          valueA = (a.email || "").toLowerCase();
          valueB = (b.email || "").toLowerCase();
          break;
        case "name":
        default:
          valueA = getUserDisplayName(a).toLowerCase();
          valueB = getUserDisplayName(b).toLowerCase();
          break;
      }

      if (typeof valueA === "string" && typeof valueB === "string") {
        return direction === "asc" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      } else {
        return direction === "asc" ? valueA - valueB : valueB - valueA;
      }
    });

    setUsersInRole(sorted);
  };

  const handleCloseUserView = () => {
    setViewUser({ open: false, loading: false, error: "", user: null });
  };

  const handleOpenUserView = async (listUser) => {
    setViewUser({ open: true, loading: true, error: "", user: listUser });
    try {
      const response = await fetch(`/api/v1/AppUsers/${listUser.id}`, {
        headers: authHeaders(),
      });
      if (!response.ok) {
        throw new Error("Could not load user details.");
      }
      const data = await response.json();
      setViewUser({ open: true, loading: false, error: "", user: data });
    } catch (err) {
      setViewUser({
        open: true,
        loading: false,
        error: err.message || "Could not load user details.",
        user: listUser,
      });
    }
  };

  const handleSort = (column) => {
    const newDirection = sortColumn === column && sortDirection === "asc" ? "desc" : "asc";
    setSortColumn(column);
    setSortDirection(newDirection);
    sortUsers(allUsersInRole, column, newDirection);
  };

  const fetchAllUsers = async () => {
    try {
      const response = await fetch("/api/v1/AppUsers", {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("accessToken")}`,
        },
      });
      if (!response.ok) throw new Error("Failed to fetch users");
      const data = await response.json();
      setAllUsers(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddRole = async () => {
    try {
      const response = await fetch("/api/v1/roles", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify(newRoleName),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to create role");
      }

      await fetchRoles();
      setOpenAddRoleDialog(false);
      setNewRoleName("");
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteRole = async (roleName) => {
    try {
      const response = await fetch(`/api/v1/roles/${roleName}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("accessToken")}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to delete role");
      }

      await fetchRoles();
      setDeleteRoleConfirm({ open: false, roleName: null });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddUsersToRole = async () => {
    try {
      const promises = selectedUsers.map((userId) =>
        fetch(`/api/v1/roles/${selectedRole.name}/users/${userId}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("accessToken")}`,
          },
        })
      );

      const results = await Promise.all(promises);
      const failed = results.find((response) => !response.ok);
      if (failed) {
        const errorBody = await failed.json().catch(() => ({}));
        throw new Error(errorBody.message || "Failed to add user to role");
      }

      await fetchUsersInRole(selectedRole.name);
      setOpenAddUsersDialog(false);
      setSelectedUsers([]);
      setUserSearchQuery("");
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRemoveUserFromRole = async (userId) => {
    try {
      const response = await fetch(`/api/v1/roles/${selectedRole.name}/users/${userId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("accessToken")}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to remove user from role");
      }

      await fetchUsersInRole(selectedRole.name);
      setRemoveUserConfirm({ open: false, userId: null, userName: null });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleOpenAddUsersDialog = async () => {
    setUserSearchQuery("");
    await fetchAllUsers();
    setOpenAddUsersDialog(true);
  };

  const handleCloseAddUsersDialog = () => {
    setOpenAddUsersDialog(false);
    setUserSearchQuery("");
  };

  const selectedRoleRequiresAd2 = Boolean(selectedRole?.requiresAssignAd2 || selectedRole?.name === "Administration_Extended");
  const canMutateSelectedRoleMembers = !selectedRoleRequiresAd2 || canAssignAd2;
  const isBuiltInRole = (role) => Boolean(role?.isBuiltIn);
  const roleSections = groupRoles(roles);
  const selectedRoleMeta = selectedRole ? getRoleMeta(selectedRole.name) : null;

  const availableUsers = useMemo(
    () =>
      allUsers
        .filter((user) => !usersInRole.some((roleUser) => roleUser.id === user.id))
        .sort((a, b) => {
          const emailA = (a.email || "").toLowerCase();
          const emailB = (b.email || "").toLowerCase();
          return emailA.localeCompare(emailB);
        }),
    [allUsers, usersInRole]
  );

  const filteredAvailableUsers = useMemo(() => {
    const query = userSearchQuery.trim().toLowerCase();
    if (!query) {
      return availableUsers;
    }

    return availableUsers.filter((user) => {
      const displayName = getUserDisplayName(user).toLowerCase();
      const email = (user.email || "").toLowerCase();
      const userName = (user.userName || "").toLowerCase();
      return displayName.includes(query) || email.includes(query) || userName.includes(query);
    });
  }, [availableUsers, userSearchQuery]);

  const handleUserSelection = (userId) => {
    setSelectedUsers((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]));
  };

  const handleSelectAll = (event) => {
    const visibleIds = filteredAvailableUsers.map((user) => user.id);
    if (event.target.checked) {
      setSelectedUsers((prev) => [...new Set([...prev, ...visibleIds])]);
    } else {
      setSelectedUsers((prev) => prev.filter((id) => !visibleIds.includes(id)));
    }
  };

  const handleUserSearchChange = (event) => {
    setUserSearchQuery(event.target.value);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Role Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      )}

      <Box display="flex" gap={2} alignItems="stretch">
        <Card sx={{ width: 340, flexShrink: 0, maxHeight: "80vh", display: "flex", flexDirection: "column" }}>
          <CardContent sx={{ pb: 1 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Roles</Typography>
              <IconButton color="primary" onClick={() => setOpenAddRoleDialog(true)} aria-label="Add role">
                <AddIcon />
              </IconButton>
            </Box>
          </CardContent>
          <Box sx={{ overflow: "auto", px: 1, pb: 1.5 }}>
            {roleSections.map((section) => (
              <Box key={section.group} sx={{ mb: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    px: 1.5,
                    py: 0.5,
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                    color: section.color,
                  }}
                >
                  {section.group}
                </Typography>
                <List dense disablePadding>
                  {section.roles.map((role) => {
                    const selected = selectedRole?.id === role.id;

                    return (
                      <ListItemButton
                        key={role.id}
                        selected={selected}
                        onClick={() => setSelectedRole(role)}
                        sx={{
                          mx: 0.5,
                          mb: 0.25,
                          borderRadius: 1.5,
                          borderLeft: "3px solid",
                          borderLeftColor: selected ? section.color : "transparent",
                          "&.Mui-selected": {
                            backgroundColor: `${section.color}18`,
                            "&:hover": {
                              backgroundColor: `${section.color}28`,
                            },
                          },
                        }}
                      >
                        <ListItemText
                          primary={role.meta.title}
                          secondary={role.name}
                          primaryTypographyProps={{
                            variant: "body2",
                            fontWeight: selected ? 700 : 600,
                            noWrap: true,
                          }}
                          secondaryTypographyProps={{
                            variant: "caption",
                            noWrap: true,
                          }}
                        />
                        <Chip
                          size="small"
                          label={role.meta.code}
                          sx={{
                            height: 22,
                            fontSize: 11,
                            fontWeight: 700,
                            mr: isBuiltInRole(role) ? 0 : 0.5,
                            color: section.color,
                            borderColor: section.color,
                            backgroundColor: "background.paper",
                          }}
                          variant="outlined"
                        />
                        {!isBuiltInRole(role) && (
                          <IconButton
                            edge="end"
                            size="small"
                            color="error"
                            aria-label={`Delete role ${role.name}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteRoleConfirm({ open: true, roleName: role.name });
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </ListItemButton>
                    );
                  })}
                </List>
              </Box>
            ))}
          </Box>
        </Card>

        {/* Right side - Users in Role */}
        <Card sx={{ flex: 1, minWidth: 0, maxHeight: "80vh", overflow: "auto" }}>
          <CardContent>
            <Tabs
              value={rightTab}
              onChange={(_event, value) => setRightTab(value)}
              sx={{ mb: 2 }}
              aria-label="Role details"
            >
              <Tab label="Users" id="role-tab-users" aria-controls="role-panel-users" />
              <Tab label="Permissions" id="role-tab-permissions" aria-controls="role-panel-permissions" />
            </Tabs>
            {rightTab === 0 && (
              <Box id="role-panel-users" role="tabpanel" aria-labelledby="role-tab-users">
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box>
                    <Typography variant="h6">
                      Users in {selectedRoleMeta?.title || selectedRole?.name || "Selected Role"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {usersInRole.length} {usersInRole.length === 1 ? "user" : "users"}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpenAddUsersDialog}
                    disabled={!selectedRole || !canMutateSelectedRoleMembers}
                    aria-label={`Add users to ${selectedRole?.name || "selected role"}`}
                  >
                    Add users
                  </Button>
                </Box>
                {selectedRoleRequiresAd2 && !canAssignAd2 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Only Administration_Extended can assign or remove this role.
                  </Alert>
                )}
                <Box
                  onClick={() => handleSort("name")}
                  sx={{
                    cursor: "pointer",
                    userSelect: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mb: 1,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1,
                    bgcolor: "grey.50",
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Name
                  </Typography>
                  {sortColumn === "name" &&
                    (sortDirection === "asc" ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />)}
                </Box>
                <List disablePadding sx={{ maxHeight: 520, overflow: "auto" }}>
                  {usersInRole.map((user) => {
                    const displayName = getUserDisplayName(user);
                    const active = isUserActive(user);

                    return (
                      <ListItem
                        key={user.id}
                        sx={{
                          mb: 0.75,
                          px: 1.5,
                          py: 1,
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          bgcolor: "background.paper",
                          "&:hover": {
                            borderColor: "primary.light",
                            backgroundColor: "action.hover",
                          },
                        }}
                        secondaryAction={
                          <Box display="flex" alignItems="center" gap={0.25}>
                            <Tooltip title="View user">
                              <IconButton
                                aria-label={`View ${displayName}`}
                                onClick={() => handleOpenUserView(user)}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                            {canMutateSelectedRoleMembers ? (
                              <Tooltip title="Remove from role">
                                <IconButton
                                  edge="end"
                                  color="error"
                                  aria-label={`Remove ${displayName} from ${selectedRole?.name}`}
                                  onClick={() =>
                                    setRemoveUserConfirm({
                                      open: true,
                                      userId: user.id,
                                      userName: displayName,
                                    })
                                  }
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                          </Box>
                        }
                      >
                        <ListItemAvatar>
                          <UserAvatar
                            profilePhotoFileId={user.profilePhotoFileId}
                            displayName={displayName}
                            size={40}
                          />
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1} pr={10}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                                {displayName}
                              </Typography>
                              <Chip
                                size="small"
                                label={active ? "Active" : "Inactive"}
                                color={active ? "success" : "default"}
                                sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
                              />
                            </Box>
                          }
                          secondary={user.email || user.userName}
                          secondaryTypographyProps={{
                            variant: "caption",
                            noWrap: true,
                          }}
                        />
                      </ListItem>
                    );
                  })}
                  {usersInRole.length === 0 && (
                    <ListItem
                      sx={{
                        py: 4,
                        borderRadius: 2,
                        border: "1px dashed",
                        borderColor: "divider",
                        justifyContent: "center",
                      }}
                    >
                      <ListItemText
                        primary="No users in this role"
                        secondary="Use the plus icon to add users."
                        primaryTypographyProps={{ align: "center", fontWeight: 600 }}
                        secondaryTypographyProps={{ align: "center" }}
                      />
                    </ListItem>
                  )}
                </List>
              </Box>
            )}
            {rightTab === 1 && (
              <Box id="role-panel-permissions" role="tabpanel" aria-labelledby="role-tab-permissions">
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Permissions for {selectedRoleMeta?.title || selectedRole?.name || "Selected Role"}
                  {selectedRoleMeta?.code ? (
                    <Chip
                      size="small"
                      label={selectedRoleMeta.code}
                      sx={{ ml: 1, fontWeight: 700, verticalAlign: "middle" }}
                    />
                  ) : null}
                </Typography>
                {permissionsLoading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <RolePermissionMatrix
                    catalog={catalog}
                    levelsByCapability={levelsByCapability}
                    canEditLocked={canAssignAd2}
                    isSaving={permissionsSaving}
                    isDirty={JSON.stringify(levelsByCapability) !== JSON.stringify(savedLevels)}
                    onLevelChange={handlePermissionLevelChange}
                    onSave={handleSavePermissions}
                  />
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Add Role Dialog */}
      <Dialog open={openAddRoleDialog} onClose={() => setOpenAddRoleDialog(false)}>
        <DialogTitle>Add New Role</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Role Name"
            fullWidth
            value={newRoleName}
            onChange={(e) => setNewRoleName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddRoleDialog(false)}>Cancel</Button>
          <Button onClick={handleAddRole} variant="contained">
            Add Role
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Users to Role Dialog */}
      <Dialog open={openAddUsersDialog} onClose={handleCloseAddUsersDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Users to {selectedRole?.name}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            margin="dense"
            label="Search users"
            value={userSearchQuery}
            onChange={handleUserSearchChange}
            inputProps={{ "aria-label": "Search users" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ mb: 1, mt: 1, display: "flex", alignItems: "center" }}>
            <Checkbox
              edge="start"
              checked={
                filteredAvailableUsers.length > 0 &&
                filteredAvailableUsers.every((user) => selectedUsers.includes(user.id))
              }
              indeterminate={
                filteredAvailableUsers.some((user) => selectedUsers.includes(user.id)) &&
                !filteredAvailableUsers.every((user) => selectedUsers.includes(user.id))
              }
              onChange={handleSelectAll}
              disabled={filteredAvailableUsers.length === 0}
              size="small"
              inputProps={{ "aria-label": "Select all visible users" }}
            />
            <Typography variant="body2" sx={{ ml: 1 }}>
              Select All{userSearchQuery.trim() ? " visible" : ""}
            </Typography>
          </Box>
          <List
            dense
            sx={{
              maxHeight: 400,
              overflow: "auto",
              "& .MuiListItem-root": {
                py: 0.5,
              },
            }}
          >
            {filteredAvailableUsers.map((user) => {
                const displayName = getUserDisplayName(user);
                return (
                <ListItem
                  key={user.id}
                  disablePadding
                  sx={{ mb: 0.5 }}
                >
                  <ListItemButton
                    onClick={() => handleUserSelection(user.id)}
                    sx={{
                      py: 1,
                      borderRadius: 1.5,
                      border: "1px solid",
                      borderColor: selectedUsers.includes(user.id) ? "primary.main" : "divider",
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <Checkbox edge="start" checked={selectedUsers.includes(user.id)} tabIndex={-1} disableRipple size="small" />
                    <ListItemAvatar>
                      <UserAvatar
                        profilePhotoFileId={user.profilePhotoFileId}
                        displayName={displayName}
                        size={36}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={displayName}
                      secondary={user.email || user.userName}
                      primaryTypographyProps={{
                        variant: "body2",
                        fontWeight: 600,
                        noWrap: true,
                      }}
                      secondaryTypographyProps={{
                        variant: "caption",
                        noWrap: true,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
              })}
            {filteredAvailableUsers.length === 0 && (
              <ListItem>
                <ListItemText
                  primary={userSearchQuery.trim() ? "No users match your search." : "No users available to add."}
                  primaryTypographyProps={{ align: "center", color: "text.secondary" }}
                />
              </ListItem>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddUsersDialog}>Cancel</Button>
          <Button onClick={handleAddUsersToRole} variant="contained" disabled={selectedUsers.length === 0}>
            Add Selected Users
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Role Confirmation Dialog */}
      <Dialog open={deleteRoleConfirm.open} onClose={() => setDeleteRoleConfirm({ open: false, roleName: null })}>
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the role "{deleteRoleConfirm.roleName}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRoleConfirm({ open: false, roleName: null })}>Cancel</Button>
          <Button onClick={() => handleDeleteRole(deleteRoleConfirm.roleName)} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <ViewRoleUserDialog
        open={viewUser.open}
        onClose={handleCloseUserView}
        user={viewUser.user}
        loading={viewUser.loading}
        error={viewUser.error}
      />

      {/* Remove User from Role Confirmation Dialog */}
      <Dialog open={removeUserConfirm.open} onClose={() => setRemoveUserConfirm({ open: false, userId: null, userName: null })}>
        <DialogTitle>Remove User from Role</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove {removeUserConfirm.userName} from the role "{selectedRole?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveUserConfirm({ open: false, userId: null, userName: null })}>Cancel</Button>
          <Button onClick={() => handleRemoveUserFromRole(removeUserConfirm.userId)} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoleManagement;
